/**
 * Ersatzteilbeschaffung -- der Vertrag.
 *
 * Zwei Vorgänge, die im Code NICHT denselben Namen tragen dürfen, weil sie sonst in der
 * Fehlersuche verschwimmen:
 *
 *   Vorgang (`OrderRecord`, @forq/orders-data)  = der interne Auftrag in FORQ
 *   SupplierPurchaseOrder (hier)                = die Bestellung BEIM Lieferanten
 *
 * Die eine unverhandelbare Regel dieses Bereichs: eine Lieferantenbestellung ohne gültigen
 * Vorgang gibt es nicht. `workOrderId` ist deshalb überall Pflicht -- im Typ, in der Eingabe,
 * in der Datenbank (`not null`) und in jeder RPC. Auch die Lagerbestellung für
 * Verbrauchsmaterial hat einen: sie läuft auf einen internen Sammelvorgang der
 * Werkstatt-Kostenstelle (Kunde 69999, Profil `internal`).
 */

import type { PriceDeviation } from './pricing';

/** Lieferant, den FORQ technisch anbinden kann. `mock` ist der Übungslieferant für Entwicklung und Tests. */
export type SupplierProvider = 'mock' | 'tvh' | 'trex';

export const supplierProviders: readonly SupplierProvider[] = ['mock', 'tvh', 'trex'];

export const supplierProviderLabels: Readonly<Record<SupplierProvider, string>> = {
  mock: 'Übungslieferant',
  tvh: 'TVH',
  trex: 'TREX.PARTS',
};

/**
 * Wofür beschafft wird.
 *
 * `work-order` -- das Teil gehört zu einem Kundenauftrag und wird dort abgerechnet.
 * `stock`      -- Verbrauchsmaterial für Lager/Werkstatt; hängt am internen Sammelvorgang und
 *                 geht beim Wareneingang ins Lager statt in den Vorgang.
 */
export type ProcurementKind = 'work-order' | 'stock';

export const procurementKindLabels: Readonly<Record<ProcurementKind, string>> = {
  'work-order': 'Für den Vorgang',
  stock: 'Für Lager und Werkstatt',
};

/** Verfügbarkeit, auf ein gemeinsames Maß gebracht -- die Rohtexte der Lieferanten bleiben im Adapter. */
export type PartsAvailability = 'in-stock' | 'partial' | 'backorder' | 'unavailable' | 'unknown';

export const partsAvailabilityLabels: Readonly<Record<PartsAvailability, string>> = {
  'in-stock': 'Sofort lieferbar',
  partial: 'Teilweise lieferbar',
  backorder: 'Nachlieferung',
  unavailable: 'Nicht lieferbar',
  unknown: 'Unbekannt',
};

/** Je höher, desto schlechter. Damit lässt sich „hat sich verschlechtert" ohne Sonderfälle prüfen. */
export const partsAvailabilityRank: Readonly<Record<PartsAvailability, number>> = {
  'in-stock': 0,
  partial: 1,
  backorder: 2,
  unknown: 3,
  unavailable: 4,
};

// ---------------------------------------------------------------------------
// Statusmaschine
// ---------------------------------------------------------------------------

export type SupplierPurchaseOrderStatus =
  | 'draft'
  | 'validating'
  | 'validated'
  | 'submitting'
  | 'ordered'
  | 'confirmed'
  | 'partially-delivered'
  | 'delivered'
  | 'failed'
  | 'requires-review'
  | 'unknown-external-state'
  | 'cancelled';

/**
 * Die erlaubten Übergänge. Diese Tabelle ist die Wahrheit -- die `case`-Anweisung in
 * `forq.parts_order_transition` MUSS sie spiegeln (Anti-Drift-Regel des Hauses).
 *
 * Zwei Wege fehlen hier mit Absicht:
 *
 *   unknown-external-state -> submitting   NIEMALS. Wer nicht weiß, ob der Lieferant die
 *                                          Bestellung hat, bestellt nicht noch einmal. Erst
 *                                          klären, dann über `draft` neu aufsetzen.
 *   ordered -> draft                       Eine abgesetzte Bestellung wird nicht wieder zum
 *                                          Entwurf. Sie wird storniert oder geklärt.
 */
export const supplierPurchaseOrderTransitions: Readonly<
  Record<SupplierPurchaseOrderStatus, readonly SupplierPurchaseOrderStatus[]>
> = {
  draft: ['validating', 'cancelled'],
  validating: ['validated', 'draft', 'requires-review', 'cancelled'],
  validated: ['submitting', 'draft', 'cancelled'],
  submitting: ['ordered', 'failed', 'unknown-external-state'],
  ordered: ['confirmed', 'partially-delivered', 'delivered', 'requires-review', 'cancelled'],
  confirmed: ['partially-delivered', 'delivered', 'requires-review', 'cancelled'],
  'partially-delivered': ['delivered', 'requires-review'],
  delivered: [],
  failed: ['draft', 'cancelled'],
  'requires-review': ['draft', 'ordered', 'failed', 'cancelled'],
  'unknown-external-state': ['requires-review', 'ordered', 'failed'],
  cancelled: [],
};

export function allowedPartsTransitionsFor(
  status: SupplierPurchaseOrderStatus,
): readonly SupplierPurchaseOrderStatus[] {
  return supplierPurchaseOrderTransitions[status] ?? [];
}

export const supplierPurchaseOrderStatusLabels: Readonly<Record<SupplierPurchaseOrderStatus, string>> = {
  draft: 'Entwurf',
  validating: 'Wird geprüft',
  validated: 'Geprüft',
  submitting: 'Wird gesendet',
  ordered: 'Bestellt',
  confirmed: 'Bestätigt',
  'partially-delivered': 'Teilweise geliefert',
  delivered: 'Geliefert',
  failed: 'Fehlgeschlagen',
  'requires-review': 'Zu klären',
  'unknown-external-state': 'Unklar beim Lieferanten',
  cancelled: 'Storniert',
};

/** Status, in denen der Vorgang die Aufmerksamkeit eines Menschen braucht -- speist den Bereich „Klärfälle". */
export const partsReviewStatuses: readonly SupplierPurchaseOrderStatus[] = [
  'requires-review',
  'unknown-external-state',
  'failed',
];

export function needsPartsReview(status: SupplierPurchaseOrderStatus): boolean {
  return partsReviewStatuses.includes(status);
}

/**
 * Ist die Bestellung beim Lieferanten schon draußen?
 *
 * Bewusst eine eigene Funktion und keine Statusliste an der Aufrufstelle: es gibt genau einen
 * Ort, an dem das entschieden wird.
 */
export function partsOrderIsPlaced(status: SupplierPurchaseOrderStatus): boolean {
  return ['ordered', 'confirmed', 'partially-delivered', 'delivered'].includes(status);
}

// ---------------------------------------------------------------------------
// Vorgangsstatus, in denen bestellt werden darf
// ---------------------------------------------------------------------------

/**
 * Die Vorgangsstatus, aus denen heraus Ersatzteile bestellt werden dürfen.
 *
 * Absichtlich NICHT `draft` (der Vorgang ist noch nicht wirklich angelegt), nicht
 * `billing-ready` (wer abrechnen will, bestellt nicht mehr nach), nicht `billed`/`completed`
 * (ein Teil, das danach kommt, gehört auf eine eigene Nachbestellung) und nicht `cancelled`.
 *
 * Diese Liste MUSS mit `forq.parts_work_order_orderable` in der Migration übereinstimmen.
 */
export const partsOrderableWorkOrderStatuses: readonly string[] = [
  'new',
  'planned',
  'assigned',
  'in-progress',
  'reported',
];

export function workOrderAllowsPartsOrder(status: string): boolean {
  return partsOrderableWorkOrderStatuses.includes(status);
}

// ---------------------------------------------------------------------------
// Lieferantenanbindung
// ---------------------------------------------------------------------------

/**
 * Die technische Anbindung eines Lieferanten je Mandant.
 *
 * Was hier NICHT steht: Zugangsdaten. `credentialRef` ist nur der NAME des Eintrags in der
 * Serverkonfiguration (`host.json` bzw. Umgebungsvariable). Der Wert dazu verlässt den
 * FORQ-Server nie -- weder in eine API-Antwort noch in ein Protokoll.
 */
export interface SupplierConnectionRecord {
  readonly id: string;
  readonly tenantId: string;
  readonly provider: SupplierProvider;
  /** Verweis in den Lieferantenstamm (`suppliers`), damit Bestellung und Eingangsrechnung zusammenfinden. */
  readonly supplierId: string | null;
  readonly supplierName: string;
  readonly active: boolean;
  /** Artikelsuche und Warenkorb beim Lieferanten freigeschaltet. */
  readonly punchoutEnabled: boolean;
  /** Elektronische Bestellübermittlung freigeschaltet -- getrennt schaltbar, weil sie später kommt. */
  readonly orderingEnabled: boolean;
  readonly credentialRef: string;
  readonly configVersion: number;
  /** Ab welcher Abweichung erneut bestätigt werden muss (Vorgabe 2 %, 5 €, 3 Tage). */
  readonly priceTolerancePercent: number;
  readonly priceToleranceAbsolute: number;
  readonly leadTimeToleranceDays: number;
  /** Lebensdauer einer PunchOut-Sitzung in Sekunden (Vorgabe 1800 = 30 Minuten). */
  readonly sessionTtlSeconds: number;
  readonly lastConnectedAt: string | null;
  readonly lastError: string;
  readonly createdAt: string;
  readonly updatedAt: string;
}

export interface SaveSupplierConnectionInput {
  readonly tenantId: string;
  readonly connectionId?: string;
  readonly provider: SupplierProvider;
  readonly supplierId?: string | null;
  readonly active?: boolean;
  readonly punchoutEnabled?: boolean;
  readonly orderingEnabled?: boolean;
  readonly credentialRef?: string;
  readonly priceTolerancePercent?: number;
  readonly priceToleranceAbsolute?: number;
  readonly leadTimeToleranceDays?: number;
  readonly sessionTtlSeconds?: number;
}

// ---------------------------------------------------------------------------
// Bestandsabgleich
// ---------------------------------------------------------------------------

/** Eine Vorgangsposition, für die der Bestand nicht reicht -- der Auslöser des ganzen Ablaufs. */
export interface PartsStockShortfall {
  readonly positionId: string;
  readonly positionNumber: number;
  readonly itemId: string | null;
  readonly itemNumber: string;
  readonly description: string;
  readonly unit: string;
  readonly requiredQuantity: number;
  readonly availableQuantity: number;
  readonly missingQuantity: number;
  readonly preferredSupplierId: string | null;
  readonly preferredSupplierName: string;
  readonly preferredSupplierItemNumber: string;
  readonly manufacturer: string;
  readonly manufacturerPartNumber: string;
}

// ---------------------------------------------------------------------------
// PunchOut
// ---------------------------------------------------------------------------

export interface StartPunchoutInput {
  readonly tenantId: string;
  readonly workOrderId: string;
  readonly provider: SupplierProvider;
  /** Optional: die Fehlmengen, mit denen der Warenkorb vorbelegt werden soll. */
  readonly shortfallPositionIds?: readonly string[];
}

/**
 * Was der Browser über eine gestartete Lieferantensitzung erfahren darf.
 *
 * Bewusst OHNE den Rückgabewert der Sitzung: der reist im PunchOut-Aufruf des Servers zum
 * Lieferanten und kommt von dort zurück. Im Browser hat er nichts zu suchen -- wer ihn dort
 * lesen kann, könnte einen Warenkorb für einen fremden Vorgang zurückschicken.
 */
export interface PunchoutSessionHandle {
  readonly sessionId: string;
  readonly provider: SupplierProvider;
  readonly workOrderId: string;
  readonly workOrderNumber: string;
  readonly correlationId: string;
  readonly expiresAt: string;
  /** Adresse, die der Browser öffnet. Leer, solange für diesen Lieferanten kein Verfahren dokumentiert ist. */
  readonly startUrl: string;
}

// ---------------------------------------------------------------------------
// Lieferantenbestellung
// ---------------------------------------------------------------------------

/** Lieferadresse -- der Browser wählt nur, WELCHE; die Werte setzt der Server. */
export type PartsDeliveryAddressKey = 'tenant' | 'work-order';

export const partsDeliveryAddressLabels: Readonly<Record<PartsDeliveryAddressKey, string>> = {
  tenant: 'Betriebsadresse',
  'work-order': 'Lieferadresse des Vorgangs',
};

export interface PartsDeliveryAddress {
  readonly key: PartsDeliveryAddressKey;
  readonly label: string;
  readonly companyName: string;
  readonly street: string;
  readonly postalCode: string;
  readonly city: string;
  readonly countryCode: string;
}

export interface SupplierPurchaseOrderLineRecord {
  readonly id: string;
  readonly purchaseOrderId: string;
  /**
   * Der Vorgang, für den diese Position beschafft wird -- mitgeführt und nicht nur über den Kopf
   * erschlossen. Damit bleibt jede einzelne Position rückführbar, auch wenn am Kopf später etwas
   * geändert oder storniert wird.
   */
  readonly workOrderId: string;
  readonly lineNumber: number;
  readonly supplierItemNumber: string;
  readonly manufacturer: string;
  readonly manufacturerPartNumber: string;
  readonly description: string;
  readonly quantity: number;
  readonly unit: string;
  readonly unitPrice: number;
  readonly lineTotal: number;
  readonly currency: string;
  readonly availability: PartsAvailability;
  readonly availabilityText: string;
  readonly leadTimeDays: number | null;
  readonly supplierReference: string;
  /** Stammartikel, falls die Lieferantenartikelnummer zugeordnet werden konnte. */
  readonly itemId: string | null;
  /** Vorgangsposition, deren Fehlmenge hiermit gedeckt wird (optional). */
  readonly orderPositionId: string | null;
  readonly receivedQuantity: number;
  /** Wann Preis und Verfügbarkeit zuletzt bestätigt wurden -- der „Preisstand". */
  readonly checkedAt: string | null;
}

export interface SupplierPurchaseOrderLineInput {
  readonly lineId?: string;
  readonly supplierItemNumber: string;
  readonly manufacturer?: string;
  readonly manufacturerPartNumber?: string;
  readonly description: string;
  readonly quantity: number;
  readonly unit?: string;
  readonly unitPrice: number;
  readonly currency?: string;
  readonly availability?: PartsAvailability;
  readonly availabilityText?: string;
  readonly leadTimeDays?: number | null;
  readonly supplierReference?: string;
  readonly itemId?: string | null;
  readonly orderPositionId?: string | null;
}

export interface SupplierPurchaseOrderRecord {
  readonly id: string;
  readonly tenantId: string;
  /** Interne Bestellnummer, z. B. `BES-2026-00042`. */
  readonly number: string;
  /** PFLICHT. Ohne gültigen Vorgang gibt es diesen Datensatz nicht. */
  readonly workOrderId: string;
  readonly workOrderNumber: string;
  readonly workOrderTitle: string;
  readonly customerName: string;
  readonly procurementKind: ProcurementKind;
  readonly provider: SupplierProvider;
  readonly supplierId: string | null;
  readonly supplierName: string;
  readonly status: SupplierPurchaseOrderStatus;
  readonly currency: string;
  readonly deliveryAddress: PartsDeliveryAddress;
  readonly shippingMethod: string;
  readonly netTotal: number;
  readonly shippingCost: number;
  /** Nettosumme + Versandkosten. Umsatzsteuer steht auf der Eingangsrechnung, nicht auf der Bestellung. */
  readonly totalAmount: number;
  readonly requestedByName: string;
  readonly submittedByName: string;
  readonly idempotencyKey: string;
  readonly correlationId: string;
  readonly externalOrderNumber: string;
  readonly punchoutSessionId: string | null;
  readonly deviations: readonly PriceDeviation[];
  readonly deviationsConfirmedAt: string | null;
  readonly priceCheckedAt: string | null;
  readonly createdAt: string;
  readonly submittedAt: string | null;
  readonly lastStatusCheckAt: string | null;
  readonly failureReason: string;
  readonly reviewReason: string;
  readonly lines: readonly SupplierPurchaseOrderLineRecord[];
}

export interface CreatePartsOrderInput {
  readonly tenantId: string;
  /** PFLICHT -- auch bei `stock`. Für Lagerbestellungen liefert `resolveStockWorkOrder` den internen Vorgang. */
  readonly workOrderId: string;
  readonly provider: SupplierProvider;
  readonly procurementKind: ProcurementKind;
  readonly deliveryAddressKey?: PartsDeliveryAddressKey;
  readonly shippingMethod?: string;
  readonly punchoutSessionId?: string | null;
  readonly lines: readonly SupplierPurchaseOrderLineInput[];
}

export interface SavePartsOrderInput {
  readonly tenantId: string;
  readonly purchaseOrderId: string;
  readonly deliveryAddressKey?: PartsDeliveryAddressKey;
  readonly shippingMethod?: string;
  readonly lines: readonly SupplierPurchaseOrderLineInput[];
}

export interface PartsOrderActionInput {
  readonly tenantId: string;
  readonly purchaseOrderId: string;
  readonly reason?: string;
}

export interface SubmitPartsOrderInput {
  readonly tenantId: string;
  readonly purchaseOrderId: string;
  /**
   * Der Schlüssel, der aus zwei Klicks eine Bestellung macht. Vom Browser erzeugt, in der
   * Datenbank eindeutig je Mandant -- ein zweiter Aufruf mit demselben Schlüssel liefert den
   * vorhandenen Vorgang zurück, statt ein zweites Mal zu bestellen.
   */
  readonly idempotencyKey: string;
}

/** Ergebnis der Prüfung unmittelbar vor dem Absenden. */
export interface PartsOrderValidation {
  readonly order: SupplierPurchaseOrderRecord;
  readonly deviations: readonly PriceDeviation[];
  /** Wesentliche Abweichung gefunden -- der Benutzer muss erneut bestätigen. */
  readonly requiresConfirmation: boolean;
  /** Konnte der Lieferant überhaupt gefragt werden? Sonst gilt der Preisstand aus dem Warenkorb. */
  readonly checked: boolean;
  readonly checkMessage: string;
}

// ---------------------------------------------------------------------------
// Wareneingang
// ---------------------------------------------------------------------------

export interface ReceivePartsLineInput {
  readonly lineId: string;
  readonly receivedQuantity: number;
}

export interface ReceivePartsInput {
  readonly tenantId: string;
  readonly purchaseOrderId: string;
  readonly lines: readonly ReceivePartsLineInput[];
  readonly note?: string;
  /** Lagerplatz für Lagerbestellungen. Bei `work-order` ohne Wirkung. */
  readonly locationId?: string | null;
}

// ---------------------------------------------------------------------------
// Ereignisprotokoll
// ---------------------------------------------------------------------------

export type PartsEventOutcome = 'ok' | 'error' | 'pending';

export interface SupplierIntegrationEventRecord {
  readonly id: string;
  readonly purchaseOrderId: string | null;
  readonly punchoutSessionId: string | null;
  readonly eventType: string;
  readonly statusBefore: SupplierPurchaseOrderStatus | null;
  readonly statusAfter: SupplierPurchaseOrderStatus | null;
  readonly actorName: string;
  /** Gefüllt, wenn kein Mensch der Auslöser war (z. B. `forq-server`). */
  readonly actorSystem: string;
  readonly occurredAt: string;
  readonly correlationId: string;
  readonly outcome: PartsEventOutcome;
  readonly errorKind: string;
  /** Bereinigt: keine Marken, keine Passwörter, keine Sitzungswerte. */
  readonly metadata: Readonly<Record<string, unknown>>;
}

// ---------------------------------------------------------------------------
// Suche
// ---------------------------------------------------------------------------

export type PartsOrderBucket = 'open' | 'review' | 'done' | 'all';

export interface PartsOrderSearchInput {
  readonly tenantId: string;
  readonly query?: string;
  readonly bucket?: PartsOrderBucket;
  readonly provider?: SupplierProvider | 'all';
  /** Nur eigene Bestellungen -- die Oberfläche setzt das, wenn nur `parts.orders.view_own` vorliegt. */
  readonly onlyOwn?: boolean;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface NormalizedPartsOrderSearchInput {
  readonly tenantId: string;
  readonly query: string;
  readonly bucket: PartsOrderBucket;
  readonly provider: SupplierProvider | 'all';
  readonly onlyOwn: boolean;
  readonly page: number;
  readonly pageSize: number;
}

export interface PartsOrderPage {
  readonly items: readonly SupplierPurchaseOrderRecord[];
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

export interface PartsOverview {
  readonly draftCount: number;
  readonly orderedCount: number;
  readonly reviewCount: number;
  readonly openReceiptCount: number;
}

/**
 * Eine Zeile des zurückgegebenen Warenkorbs, wie der Übungsshop sie schickt.
 *
 * Nur Artikelnummer und Menge -- Preise, Bezeichnungen und Verfügbarkeiten kommen aus dem
 * Adapter. Was der Browser schickt, wird nie als Preis übernommen.
 */
export interface CompletePunchoutCartLine {
  readonly supplierItemNumber: string;
  readonly quantity: number;
}

/**
 * Der Rückweg des Warenkorbs.
 *
 * ACHTUNG, Sonderfall: Nur der Übungslieferant darf hier durch. Ein echter Lieferant schickt
 * seinen Warenkorb direkt an den FORQ-Server, ohne den Browser -- deshalb kennt der Browser bei
 * echten Lieferanten den Sitzungswert gar nicht (siehe `PunchoutSessionHandle`). Der Übungsshop
 * läuft dagegen im eigenen Browser und muss ihn tragen. Server und Demo-Gateway weisen einen
 * Aufruf für `tvh` oder `trex` deshalb ab.
 */
export interface CompletePunchoutInput {
  readonly tenantId: string;
  readonly sessionToken: string;
  readonly lines: readonly CompletePunchoutCartLine[];
}

/** Der interne Sammelvorgang für Lagerbestellungen. */
export interface StockWorkOrderHandle {
  readonly workOrderId: string;
  readonly workOrderNumber: string;
  readonly title: string;
}

export interface ResolveReviewInput extends PartsOrderActionInput {
  readonly toStatus: SupplierPurchaseOrderStatus;
  readonly externalOrderNumber?: string;
}

/**
 * Der Bestelldienst -- alles, was den Lieferanten wirklich beruehrt.
 *
 * Diese fuenf Schritte kann NUR der FORQ-Server: er allein hat die Zugangsdaten und
 * er allein darf die abschliessenden Datenbankfunktionen aufrufen (`service_role`).
 * Fehlt er -- in der Cloud-Fassung bis zum Umstieg --, ist `available` false und die
 * Oberflaeche sagt das im Klartext, statt einen Knopf anzubieten, der nichts tut.
 */
export interface PartsOrderingService {
  readonly available: boolean;
  startPunchout(input: StartPunchoutInput): Promise<PunchoutSessionHandle>;
  completePunchout(input: CompletePunchoutInput): Promise<SupplierPurchaseOrderRecord>;
  validate(input: PartsOrderActionInput): Promise<PartsOrderValidation>;
  submit(input: SubmitPartsOrderInput): Promise<SupplierPurchaseOrderRecord>;
  refreshStatus(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord>;
}

/** Die Meldung, wenn der Bestelldienst fehlt. An einer Stelle, damit sie überall gleich lautet. */
export const partsServiceMissingMessage =
  'Das Bestellen läuft über den FORQ-Server. In dieser Fassung sind Entwurf, Bestandsprüfung und Historie verfügbar, das Absenden nicht.';

// ---------------------------------------------------------------------------
// Der Port
// ---------------------------------------------------------------------------

export interface PartsGateway {
  getOverview(tenantId: string): Promise<PartsOverview>;

  /** Konfiguration -- nur mit `parts.admin`, liefert nie Zugangsdaten. */
  listConnections(tenantId: string): Promise<readonly SupplierConnectionRecord[]>;
  saveConnection(input: SaveSupplierConnectionInput): Promise<SupplierConnectionRecord>;

  /** Bestandsabgleich der Vorgangspositionen -- der Auslöser („4 Stück fehlen"). */
  checkWorkOrderStock(tenantId: string, workOrderId: string): Promise<readonly PartsStockShortfall[]>;

  /** Der interne Sammelvorgang der Werkstatt für Lagerbestellungen. Legt ihn an, wenn es ihn nicht gibt. */
  resolveStockWorkOrder(tenantId: string, costCenterId?: string | null): Promise<StockWorkOrderHandle>;

  startPunchoutSession(input: StartPunchoutInput): Promise<PunchoutSessionHandle>;
  /** Warenkorb des Übungsshops zurückgeben. Für echte Lieferanten gesperrt -- die posten direkt an den Server. */
  completePunchout(input: CompletePunchoutInput): Promise<SupplierPurchaseOrderRecord>;

  createDraft(input: CreatePartsOrderInput): Promise<SupplierPurchaseOrderRecord>;
  saveDraft(input: SavePartsOrderInput): Promise<SupplierPurchaseOrderRecord>;
  /** Preis und Verfügbarkeit unmittelbar vor dem Absenden erneut prüfen. */
  validateOrder(input: PartsOrderActionInput): Promise<PartsOrderValidation>;
  /** Der Benutzer hat die gefundenen Abweichungen gesehen und bestätigt. */
  confirmDeviations(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord>;
  submitOrder(input: SubmitPartsOrderInput): Promise<SupplierPurchaseOrderRecord>;
  cancelOrder(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord>;

  getOrder(tenantId: string, purchaseOrderId: string): Promise<SupplierPurchaseOrderRecord>;
  listOrders(input: PartsOrderSearchInput): Promise<PartsOrderPage>;
  listForWorkOrder(tenantId: string, workOrderId: string): Promise<readonly SupplierPurchaseOrderRecord[]>;
  listEvents(tenantId: string, purchaseOrderId: string): Promise<readonly SupplierIntegrationEventRecord[]>;

  /** Klärfälle: Status beim Lieferanten nachfragen bzw. von Hand entscheiden. */
  refreshStatus(input: PartsOrderActionInput): Promise<SupplierPurchaseOrderRecord>;
  resolveReview(input: ResolveReviewInput): Promise<SupplierPurchaseOrderRecord>;

  receiveGoods(input: ReceivePartsInput): Promise<SupplierPurchaseOrderRecord>;
}

// ---------------------------------------------------------------------------
// Reine Helfer -- von Demo-Gateway, Adapter und Oberfläche gemeinsam benutzt
// ---------------------------------------------------------------------------

function money(value: number): number {
  return Math.round(value * 100) / 100;
}

function quantityOf(value: number): number {
  return Math.round(value * 1000) / 1000;
}

const availabilities = new Set<PartsAvailability>([
  'in-stock',
  'partial',
  'backorder',
  'unavailable',
  'unknown',
]);

/** Eine geprüfte Bestellposition -- Ergebnis von `normalizePartsOrderLine`. */
export interface NormalizedPartsOrderLine extends SupplierPurchaseOrderLineInput {
  readonly lineNumber: number;
  readonly lineTotal: number;
  readonly unit: string;
  readonly currency: string;
  readonly availability: PartsAvailability;
}

/**
 * Eine Bestellposition prüfen und in Form bringen.
 *
 * Wirft bei allem, was aus einer Lieferantenrückgabe kommen kann und keine Position ergibt:
 * fehlende Artikelnummer, Menge null oder negativ, negativer Preis, Zahl, die keine ist.
 * Absichtlich streng -- die Daten stammen von außen und werden nie ungeprüft übernommen.
 */
export function normalizePartsOrderLine(
  input: SupplierPurchaseOrderLineInput,
  lineNumber: number,
): NormalizedPartsOrderLine {
  const supplierItemNumber = input.supplierItemNumber?.trim() ?? '';
  const description = input.description?.trim() ?? '';
  const rawQuantity = Number(input.quantity);
  const rawPrice = Number(input.unitPrice);

  if (!supplierItemNumber) {
    throw new Error('Eine Bestellposition benötigt eine Lieferantenartikelnummer.');
  }
  if (supplierItemNumber.length > 80) {
    throw new Error('Die Lieferantenartikelnummer ist zu lang.');
  }
  if (!description) {
    throw new Error('Eine Bestellposition benötigt eine Bezeichnung.');
  }
  if (!Number.isFinite(rawQuantity) || rawQuantity <= 0) {
    throw new Error('Die Bestellmenge muss größer als null sein.');
  }
  if (rawQuantity > 100000) {
    throw new Error('Die Bestellmenge ist unglaubwürdig hoch -- bitte prüfen.');
  }
  if (!Number.isFinite(rawPrice) || rawPrice < 0) {
    throw new Error('Der Einzelpreis darf nicht negativ sein.');
  }
  if (rawPrice > 1000000) {
    throw new Error('Der Einzelpreis ist unglaubwürdig hoch -- bitte prüfen.');
  }

  const availability =
    input.availability && availabilities.has(input.availability) ? input.availability : 'unknown';
  const leadTimeDays =
    input.leadTimeDays == null || !Number.isFinite(Number(input.leadTimeDays))
      ? null
      : Math.max(0, Math.trunc(Number(input.leadTimeDays)));
  const normalizedQuantity = quantityOf(rawQuantity);
  const normalizedPrice = money(rawPrice);

  return {
    ...(input.lineId ? { lineId: input.lineId } : {}),
    lineNumber,
    supplierItemNumber,
    manufacturer: input.manufacturer?.trim() ?? '',
    manufacturerPartNumber: input.manufacturerPartNumber?.trim() ?? '',
    description: description.slice(0, 400),
    quantity: normalizedQuantity,
    unit: input.unit?.trim() || 'Stück',
    unitPrice: normalizedPrice,
    lineTotal: money(normalizedQuantity * normalizedPrice),
    currency: (input.currency?.trim() || 'EUR').toUpperCase(),
    availability,
    availabilityText: input.availabilityText?.trim().slice(0, 200) ?? '',
    leadTimeDays,
    supplierReference: input.supplierReference?.trim().slice(0, 120) ?? '',
    itemId: input.itemId ?? null,
    orderPositionId: input.orderPositionId ?? null,
  };
}

/**
 * Der ganze Warenkorb, geprüft und in Form gebracht.
 *
 * Der eine Ort, an dem eine Punchback-Rückgabe zur Bestellung wird -- deshalb hier und nicht
 * verteilt: gleiche Prüfung im Demo-Gateway, im Adapter und (gespiegelt) in SQL.
 */
export function normalizePartsOrderLines(
  lines: readonly SupplierPurchaseOrderLineInput[],
): readonly NormalizedPartsOrderLine[] {
  if (lines.length === 0) {
    throw new Error('Eine Bestellung ohne Positionen gibt es nicht.');
  }
  if (lines.length > 500) {
    throw new Error('Eine Bestellung mit mehr als 500 Positionen wird nicht angenommen.');
  }
  return lines.map((line, index) => normalizePartsOrderLine(line, index + 1));
}

export interface PartsOrderTotals {
  readonly netTotal: number;
  readonly shippingCost: number;
  readonly totalAmount: number;
}

export function calculatePartsOrderTotals(
  lines: readonly { readonly quantity: number; readonly unitPrice: number }[],
  shippingCost: number,
): PartsOrderTotals {
  const netTotal = money(lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0));
  const shipping = money(Number.isFinite(shippingCost) && shippingCost > 0 ? shippingCost : 0);
  return { netTotal, shippingCost: shipping, totalAmount: money(netTotal + shipping) };
}

export function normalizeCreatePartsOrderInput(input: CreatePartsOrderInput): CreatePartsOrderInput {
  if (!input.workOrderId?.trim()) {
    throw new Error(
      'Eine Ersatzteilbestellung braucht einen Vorgang. Bitte den Auftrag öffnen und von dort bestellen.',
    );
  }
  return {
    tenantId: input.tenantId,
    workOrderId: input.workOrderId.trim(),
    provider: input.provider,
    procurementKind: input.procurementKind,
    deliveryAddressKey: input.deliveryAddressKey ?? 'tenant',
    shippingMethod: input.shippingMethod?.trim() ?? '',
    punchoutSessionId: input.punchoutSessionId ?? null,
    lines: normalizePartsOrderLines(input.lines),
  };
}

export function normalizeSavePartsOrderInput(input: SavePartsOrderInput): SavePartsOrderInput {
  if (!input.purchaseOrderId?.trim()) {
    throw new Error('Zum Speichern fehlt die Bestellung.');
  }
  return {
    tenantId: input.tenantId,
    purchaseOrderId: input.purchaseOrderId.trim(),
    deliveryAddressKey: input.deliveryAddressKey ?? 'tenant',
    shippingMethod: input.shippingMethod?.trim() ?? '',
    lines: normalizePartsOrderLines(input.lines),
  };
}

export function normalizePartsOrderSearchInput(
  input: PartsOrderSearchInput,
): NormalizedPartsOrderSearchInput {
  return {
    tenantId: input.tenantId,
    query: input.query?.trim() ?? '',
    bucket: input.bucket ?? 'open',
    provider: input.provider ?? 'all',
    onlyOwn: input.onlyOwn ?? false,
    page: Math.max(1, Math.trunc(input.page ?? 1)),
    pageSize: Math.min(100, Math.max(1, Math.trunc(input.pageSize ?? 25))),
  };
}

/**
 * Ein Idempotenzschlüssel für einen Absendevorgang.
 *
 * `crypto.randomUUID` und keine Zeit-plus-Zufall-Bastelei: der Wert muss auch dann eindeutig
 * sein, wenn zwei Arbeitsplätze in derselben Millisekunde auf denselben Knopf drücken.
 */
export function createIdempotencyKey(): string {
  return globalThis.crypto.randomUUID();
}

export function normalizeReceivePartsInput(input: ReceivePartsInput): ReceivePartsInput {
  if (input.lines.length === 0) {
    throw new Error('Für den Wareneingang muss mindestens eine Position eine Menge tragen.');
  }
  const lines = input.lines.map((line) => {
    const received = Number(line.receivedQuantity);
    if (!Number.isFinite(received) || received < 0) {
      throw new Error('Eine eingegangene Menge darf nicht negativ sein.');
    }
    return { lineId: line.lineId, receivedQuantity: quantityOf(received) };
  });
  if (lines.every((line) => line.receivedQuantity === 0)) {
    throw new Error('Für den Wareneingang muss mindestens eine Position eine Menge tragen.');
  }
  return {
    tenantId: input.tenantId,
    purchaseOrderId: input.purchaseOrderId,
    lines,
    note: input.note?.trim() ?? '',
    locationId: input.locationId ?? null,
  };
}

/**
 * Der Status, der sich aus den gelieferten Mengen ergibt.
 *
 * Reine Rechnung, damit Demo-Gateway und SQL dieselbe Antwort geben: alles da = `delivered`,
 * etwas da = `partially-delivered`, nichts da = der Status bleibt, wie er war.
 */
export function partsDeliveryStatusFor(
  lines: readonly { readonly quantity: number; readonly receivedQuantity: number }[],
  current: SupplierPurchaseOrderStatus,
): SupplierPurchaseOrderStatus {
  const received = lines.reduce((sum, line) => sum + line.receivedQuantity, 0);
  if (received <= 0) return current;
  const complete = lines.every((line) => line.receivedQuantity >= line.quantity);
  return complete ? 'delivered' : 'partially-delivered';
}
